import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import useManageView from '@app/components/common/modals/ManageViewModal/hooks/useManageView';
import { CatButton } from '@blocks-react/components/Button';
import { CatGrid } from '@blocks-react/components/Grid';
import { CatGridItem } from '@blocks-react/components/GridItem';
import { CatCheckboxField } from '@blocks-react/components/CheckboxField';
import { CatIconSettings } from '@blocks-react/components/Icons/Settings';
import { CatPopover } from '@blocks-react/components/Popover';
import { loadSavedListDetails } from '@app/store/account/actions';
import {
  SAVED_LIST_MANAGE_VIEW_FIELDS,
  SAVED_LIST,
  SAVED_LIST_MANAGE_VIEW_SCHEMA
} from '../../constants';
import { BUTTON_VARIANTS } from '@app/constants/commonConstants';
import styles from './ManageView.module.scss';

const ManageView = () => {
  const [t] = useTranslation();
  const [showManageViewPopup, setShowManageViewPopup] = useState(false);
  const searchOptions = useSelector(s => s.account.searchOptions);
  const dispatch = useDispatch();
  const { saveManageViewPreferences } = useManageView({
    uniquePrefix: SAVED_LIST,
    columns: SAVED_LIST_MANAGE_VIEW_SCHEMA
  });
  const manageViewTitle = t('MANAGE_VIEW');
  const { handleSubmit, register, getValues, setValue, reset } = useForm({
    defaultValues: {
      [SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS]:
        searchOptions?.showMyFavs || false,
      [SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS]: !searchOptions
        ? true
        : searchOptions?.showMyLists
    }
  });
  const onSubmit = data => {
    saveManageViewPreferences(data);
    const showMyLists = data[SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS];
    const showMyFavs = data[SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS];
    dispatch(
      loadSavedListDetails(
        {
          ...searchOptions,
          showMyLists,
          showMyFavs
        },
        showMyLists,
        true
      )
    );
  };

  const onClose = () => {
    reset();
    setShowManageViewPopup(popoverState => !popoverState);
  };
  return (
    <CatPopover
      heading={manageViewTitle}
      position="auto"
      behavior="responsive"
      isActive={showManageViewPopup}
      onBlPopoverOpen={() => setShowManageViewPopup(true)}
      onBlPopoverClose={() => setShowManageViewPopup(false)}
    >
      <CatButton slot="trigger" variant={BUTTON_VARIANTS.GHOST}>
        {manageViewTitle}
        <CatIconSettings slot="before" />
      </CatButton>
      <div className="cat-u-display-flex flex-column text-wrap">
        <span
          className={`cat-u-margin-bottom-lg ${styles['manage-view-text']}`}
        >
          {t('MANAGE_VIEW_FAV_SAVE_PAGE')}
        </span>
        <form onSubmit={handleSubmit(onSubmit)} className="w-100">
          <CatGrid>
            <CatGridItem>
              <CatCheckboxField
                name={SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS}
                {...register(SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS)}
                checked={getValues(SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_FAVS)}
                onBlCheckboxChange={e =>
                  setValue(e.target.name, e.target.checked)
                }
              >
                {t('SHOW_MY_FAVORITES')}
              </CatCheckboxField>
              <CatCheckboxField
                name={SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS}
                {...register(SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS)}
                checked={getValues(SAVED_LIST_MANAGE_VIEW_FIELDS.SHOW_MY_LISTS)}
                onBlCheckboxChange={e =>
                  setValue(e.target.name, e.target.checked)
                }
              >
                {t('SHOW_MY_LISTS')}
              </CatCheckboxField>
            </CatGridItem>
          </CatGrid>
          <CatButton className="cat-u-margin-top-xl" type="submit">
            {t('UPDATE')}
          </CatButton>
          <CatButton
            className="cat-u-margin-top-xl"
            variant={BUTTON_VARIANTS.GHOST}
            onClick={() => onClose()}
          >
            {t('CANCEL')}
          </CatButton>
        </form>
      </div>
    </CatPopover>
  );
};

ManageView.propTypes = {
  setShowManageViewPopup: PropTypes.func
};

export default ManageView;
